<template>
  <v-card>
    <v-card-title>
      <div class="card-title">
        <span class="bold table-card-title">{{ tableTitle }}</span>

        <div class="status-search-group">
          <mf-status-filter v-if="itemsFilter.length > 1" :items="itemsFilter" @input="getFilterResult" />
          <div class="min-width-345">
            <mf-search v-model="search" class="pt-0 mt-0" hide-details placeholder="Buscar" />
          </div>
        </div>
      </div>
    </v-card-title>

    <v-card-text class="pa-0">
      <paginated-data-table
        ref="table"
        :headers="headers"
        :search="search"
        :query="query"
        :custom-items-per-page="10"
        :query-variables="queryVariables"
        query-name="findPaginatedBenefits"
        query-main-property-name="benefits"
        :graphql-uri="$microservicesUrls['benefitsManager']"
        use-input-param
      >
        <template v-slot:item="{ item }">
          <tr class="colorful-border" :style="{ borderColor: getStatusLabel(item.status).color }">
            <td>
              <mf-icon fill small :color="getStatusLabel(item.status).color">fiber_manual_record</mf-icon>
              {{ getStatusLabel(item.status).text }}
            </td>
            <td class="nowrap-row" style="max-width: 300px">
              <v-tooltip max-width="250" top content-class="truncated-tooltip">
                <template v-slot:activator="{ on }">
                  <span v-on="on">
                    <router-link v-if="!(isFranchisee && item.status === 'SCHEDULED')" class="link" :to="{ path: link(item._id, item.status) }">
                      {{ item.title }}
                    </router-link>
                    <span v-else>{{ item.title }}</span>
                  </span>
                </template>
                <span>{{ item.title }}</span>
              </v-tooltip>
            </td>
            <td class="nowrap-row" style="max-width: 300px">
              <v-tooltip :disabled="!item.description" max-width="250" top content-class="truncated-tooltip">
                <template v-slot:activator="{ on }">
                  <span v-on="on">{{ (item.description || '-') | truncate({ length: 150 }) }}</span>
                </template>
                <span>{{ item.description || '-' }}</span>
              </v-tooltip>
            </td>
            <td class="coupon-name">{{ item.code | hifen }}</td>
            <td>
              <mf-tooltip top max-width="250" :disabled="item.unitsInfo.length === 0">
                <template v-slot:activator="{ on }">
                  <span v-on="on">
                    {{ item.unitsInfo.length === 0 ? '-' : item.unitsInfo.length === 1 ? '1 loja' : `${item.unitsInfo.length} lojas` }}
                  </span>
                </template>
                <div v-for="unit in item.unitsInfo" :key="unit.id_erp">
                  <span>{{ unit.name }}</span>
                </div>
              </mf-tooltip>
            </td>
            <td>{{ item.products.length ? 'Sim' : 'Não' }}</td>
            <td>{{ getPeriod(item.startDate, item.expiresAt) }}</td>
            <td class="text-right">
              <v-menu v-if="!(isFranchisee && item.status === 'SCHEDULED')" :close-on-content-click="false">
                <template v-slot:activator="{ on }">
                  <mf-button
                    data-userflow-id="coupon-discount-vertical-menu-activator"
                    type="icon"
                    icon="more_vert"
                    color="cinza-0"
                    v-on:click.native="on.click"
                  />
                </template>

                <v-list data-userflow-id="coupon-discount-vertical-menu">
                  <v-list-item v-if="item.status !== 'FINISHED' && !isFranchisee" @click="$goTo(link(item._id))">
                    <v-list-item-title>Editar</v-list-item-title>
                  </v-list-item>
                  <v-list-item v-if="item.status === 'FINISHED' && !isFranchisee" @click="$goTo(link(item._id))">
                    <v-list-item-title>Visualizar</v-list-item-title>
                  </v-list-item>
                  <v-list-item v-if="(item.status === 'ACTIVE' || item.status === 'SCHEDULED') && !isFranchisee" @click="openCommunicateModal(item._id)">
                    <v-list-item-title>Comunicar</v-list-item-title>
                  </v-list-item>
                  <v-list-item v-if="item.status === 'SCHEDULED' && !isFranchisee" @click="mutateBenefit(item._id, 'unschedule')">
                    <v-list-item-title>Suspender</v-list-item-title>
                  </v-list-item>
                  <v-list-item v-if="item.status === 'DRAFT' && !isFranchisee" @click="mutateBenefit(item._id, 'delete', item.title)">
                    <v-list-item-title>Deletar</v-list-item-title>
                  </v-list-item>
                  <v-list-item v-if="item.status === 'ACTIVE' && !isFranchisee" @click="mutateBenefit(item._id, 'finish')">
                    <v-list-item-title>Finalizar</v-list-item-title>
                  </v-list-item>
                  <v-list-item v-if="!isFranchisee" @click="cloneBenefit(item._id)">
                    <v-list-item-title>Duplicar</v-list-item-title>
                  </v-list-item>
                  <v-list-item v-if="item.status === 'ACTIVE' || item.status === 'FINISHED'" @click="$goTo(results(item._id))">
                    <v-list-item-title>Resultados</v-list-item-title>
                  </v-list-item>
                  <v-list-item v-if="item.status === 'ACTIVE' || item.status === 'FINISHED'" @click="$goTo(usageHistory(item._id))">
                    <v-list-item-title>Histórico de utilização</v-list-item-title>
                  </v-list-item>
                  <v-list-item v-if="!isFranchisee" @click="openLogsModal(item._id)">
                    <v-list-item-title>Logs</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </td>
          </tr>
        </template>
      </paginated-data-table>
      <communicate-modal v-model="communicateDialog" :benefitId="benefitId" />
      <logs-modal v-model="logsDialog" tags="BENEFIT" log-title="cupons de desconto" :id-item="benefitId" />
    </v-card-text>
  </v-card>
</template>

<script>
import LogsModal from '@/components/logs-modal/LogsModal.vue'
import PaginatedDataTable from '@/components/PaginatedDataTable.vue'
import { DateTime } from 'luxon'
import { getStatusLabel, getPeriod } from '@/config/helpers.js'
import { QUERY_FIND_BENEFITS, UNSCHEDULE_BENEFIT, DELETE_BENEFIT, CLONE_BENEFIT, FINISH_ACTIVE_BENEFIT } from '../../../graphql'

export default {
  components: {
    PaginatedDataTable,
    LogsModal,
    CommunicateModal: () => import('@/modules/campanhas/cupom-desconto/views/components/edit/CommunicateModal.vue'),
  },
  filters: {
    hifen(value) {
      if (!value) return '-'
      return value
    },
  },
  props: {
    logsTag: {
      type: String,
      default: '',
    },
    itemsFilter: {
      type: Array,
      default: () => [],
    },
    history: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    search: '',
    benefitId: '',
    logsDialog: false,
    DateTime: DateTime,
    statusToSearch: [],
    communicateDialog: false,
  }),

  computed: {
    tableTitle() {
      return this.isFranchisee ? 'Campanhas de cupom da rede' : 'Campanhas de cupom'
    },
    isFranchise() {
      return this.$getLoggedUser().is_franchise
    },
    isFranchisee() {
      return this.$getLoggedUser().is_franchisee
    },
    parsedStatus() {
      return this.statusToSearch ? this.statusToSearch : []
    },
    query() {
      return QUERY_FIND_BENEFITS
    },
    queryVariables() {
      return {
        franchise: this.isFranchise || this.isFranchisee,
        status: this.history ? ['FINISHED'] : this.parsedStatus,
      }
    },
    headers() {
      return [
        {
          text: 'Status',
          value: 'status',
          width: '150px',
        },
        {
          text: 'Nome',
          value: 'title',
          width: '20%',
        },
        {
          text: 'Descrição',
          value: 'description',
          width: '20%',
        },
        {
          text: 'Cupom',
          value: 'code',
          width: '10%',
        },
        {
          text: 'Lojas',
          value: 'units',
          width: '90px',
          sortable: false,
        },
        {
          text: 'Produtos vigentes',
          value: 'products',
          width: '175px',
        },
        {
          text: 'Data de validade',
          value: 'expiresAt',
          width: '20%',
        },
        {
          text: '',
          sortable: false,
          width: '5%',
        },
      ]
    },
  },
  watch: {
    search() {
      this.$emit('change-search')
    },
  },
  beforeMount() {
    this.statusToSearch = this.itemsFilter.map((item) => item.value)
  },
  methods: {
    getPeriod,
    getStatusLabel,
    getFilterResult(value) {
      this.statusToSearch = value.length ? value : this.itemsFilter.map((item) => item.value)
    },
    openLogsModal(id) {
      this.benefitId = id
      this.logsDialog = true
    },
    async cloneBenefit(id) {
      const confirmed = await this.$confirm(this.$t(`campanhas.confirmations.clone`))
        .then(() => true)
        .catch(() => false)
      if (!confirmed) return
      this.$refs.table.paginationLoading = true
      await this.$apollo
        .mutate({
          mutation: CLONE_BENEFIT,
          variables: {
            input: { id },
          },
          context: {
            uri: this.$microservicesUrls['benefitsManager'],
          },
        })
        .then(({ data: { cloneBenefit } }) => {
          this.$router.push(`/campanhas/cupom-desconto/${cloneBenefit}`).catch(() => {})
        })
        .catch(() => {
          this.$snackbar(this.$t('campanhas.snackbars.errors.clone'))
        })
    },
    async mutateBenefit(id, action, name) {
      const confirm = this.$t(`campanhas.confirmations.${action}`)
      if (name) confirm.message = `Tem certeza que deseja remover a campanha "${name}"`
      const confirmed = await this.$confirm(confirm)
        .then(() => true)
        .catch(() => false)
      if (!confirmed) return

      const actions = {
        unschedule: UNSCHEDULE_BENEFIT,
        delete: DELETE_BENEFIT,
        finish: FINISH_ACTIVE_BENEFIT,
      }

      this.$refs.table.paginationLoading = true
      await this.$apollo
        .mutate({
          mutation: actions[action],
          variables: {
            input: { id },
          },
          context: {
            uri: this.$microservicesUrls['benefitsManager'],
          },
        })
        .then(() => {
          this.$refs.table.waitAndGetData()
        })
        .catch(() => {
          this.$snackbar(this.$t(`campanhas.snackbars.errors.${action}`))
        })
      this.$refs.table.paginationLoading = false
    },
    hasResults(status) {
      return ['ACTIVE', 'FINISHED'].includes(status)
    },
    link(id, status = '') {
      if (this.isFranchisee) return `/campanhas/cupom-desconto/resultados/${id}`
      if (status && status === 'FINISHED') return `/campanhas/cupom-desconto/resultados/${id}`
      return `/campanhas/cupom-desconto/${id}`
    },
    usageHistory(id) {
      return `/campanhas/cupom-desconto/historico-utilizacao/${id}`
    },
    results(id) {
      return `/campanhas/cupom-desconto/resultados/${id}`
    },
    openCommunicateModal(id) {
      this.benefitId = id
      this.communicateDialog = true
    },
  },
}
</script>

<style lang="scss">
.card-title {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding-left: 0;
  padding-bottom: 16px;
}

.status-search-group {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;
}

.industry-striped {
  tr:nth-child(even) {
    background-color: #f7f7f7;
  }
}

.text-align-right {
  text-align: right;
  margin-right: 12px;
}

.colorful-border {
  border-left: 3px solid;
}

.coupon-name {
  font-weight: 500 !important;
  color: $marca-0-l10 !important;
}
</style>
